import { bindImpact } from '@/api/account';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { getSetLocalStorage } from '@/utils/tools';
import sha1 from 'crypto-js/sha1';
import { Provider, ProviderType, TrackingEvent } from '../types';

type ImpactIdentify = (
  command: 'identify',
  identity: {
    customerId: string;
    customerEmail: string;
    customProfileId: string;
  },
) => void;

interface ImpactConfig {
  isEnabled?: boolean;
}

interface ImpactBindOptions {
  clearIrclickidOnSuccess?: boolean;
}

export class ImpactRadiusProvider implements Provider {
  type = ProviderType.IMPACT_RADIUS;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private lastIdentifyPayload: string | null = null;
  private config: ImpactConfig;

  constructor(config: ImpactConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      this.initialized = true;
    } catch (error) {
      logger.error('Failed to initialize Impact Radius:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    // Impact Radius 的事件追踪都是靠后端的
    if (this.loggingEnabled) {
      logger.log('Impact Radius tracking event:', event);
    }
  }

  identify(user: { id?: number; email?: string } = {}, customProfileId = ''): void {
    if (!this.isEnabled || env.NEXT_PUBLIC_SITE_NAME !== 'us' || typeof window === 'undefined')
      return;
    const ire = (window as Window & { ire?: ImpactIdentify }).ire;
    if (typeof ire !== 'function') return;
    const customerId =
      typeof user.id === 'number' && Number.isFinite(user.id) && user.id > 0 ? String(user.id) : '';
    const email = typeof user.email === 'string' ? user.email.trim().toLowerCase() : '';

    const identity = {
      customerId,
      customerEmail: email ? sha1(email).toString() : '',
      customProfileId: customProfileId.trim(),
    };
    const identityKey = JSON.stringify(identity);
    if (identityKey === this.lastIdentifyPayload) return;

    try {
      ire('identify', identity);
      this.lastIdentifyPayload = identityKey;
    } catch (error) {
      logger.error('Impact UTT identify failed:', error);
    }
  }

  async bindImpact(
    onSuccess?: () => void,
    { clearIrclickidOnSuccess = true }: ImpactBindOptions = {},
  ): Promise<void> {
    // 绑定impact
    // 从 URL 参数或 localStorage 获取 irclickid
    const urlParams = new URLSearchParams(window.location.search);
    const urlIrclickid = urlParams.get('irclickid');
    const irclickid = urlIrclickid || getSetLocalStorage.get('irclickid');

    if (irclickid) {
      if (urlIrclickid) {
        const date = new Date().getTime();
        // 新点击设置 30 天有效期；缓存回读不续期，避免归因窗口无限延长。
        getSetLocalStorage.set('irclickid', irclickid, date + 30 * 24 * 60 * 60 * 1000);
      }

      try {
        // 【1008860】前端判断传参为空，不调接口
        const bindRes = irclickid ? await bindImpact({ irclickid }) : {};

        // 绑定成功
        if (bindRes?.code === 200) {
          if (onSuccess) {
            onSuccess();
          }
          if (clearIrclickidOnSuccess) {
            // 登录态绑定成功后删除缓存；游客注册下单前必须保留归因信息。
            localStorage.removeItem('irclickid');
          }
        }
      } catch (e) {
        logger.error('bindImpact error', e);
      }
    }
  }

  /**
   * 启用Impact日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用Impact日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
